import { environment } from "../../configuration";
import { api, getToken } from "../api";
import type { DataQuestionStreamEvent } from "../../store/api/dataQuestionsApi";

export interface AskDataQuestionInput {
  question: string;
  threadId?: number;
}

const ASK_URL = `${environment.apiUrl}/admin/data-questions/ask`;

export const STREAM_CUT_MESSAGE =
  "La conexión se cortó antes de terminar la respuesta. Volvé a intentar.";
export const SESSION_EXPIRED_MESSAGE =
  "Tu sesión expiró. Volvé a iniciar sesión e intentá de nuevo.";

/**
 * Splits complete SSE frames (`data: {...}\n\n`) off the buffer, hands each
 * parsed event to `onEvent`, and returns whatever partial frame is left.
 */
const drainFrames = (
  buffer: string,
  onEvent: (event: DataQuestionStreamEvent) => void
): string => {
  let rest = buffer.replace(/\r\n/g, "\n");
  let separator = rest.indexOf("\n\n");
  while (separator !== -1) {
    const frame = rest.slice(0, separator);
    rest = rest.slice(separator + 2);
    const data = frame
      .split("\n")
      .filter((line) => line.startsWith("data:"))
      .map((line) => line.slice("data:".length).trim())
      .join("\n");
    if (data) {
      try {
        onEvent(JSON.parse(data) as DataQuestionStreamEvent);
      } catch {
        // A malformed frame is dropped; the stream still ends with result/error.
      }
    }
    separator = rest.indexOf("\n\n");
  }
  return rest;
};

const post = (body: string, signal?: AbortSignal) =>
  fetch(ASK_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      Authorization: `Bearer ${getToken() ?? ""}`,
    },
    body,
    signal,
  });

/**
 * `fetch` because EventSource cannot POST a body. On a 401 the shared axios
 * instance is poked once so its interceptor refreshes the access token (or
 * hard-logs-out when the refresh token is dead too), then the stream is
 * retried with the new token.
 */
const authorizedPost = async (
  body: string,
  signal?: AbortSignal
): Promise<Response> => {
  const first = await post(body, signal);
  if (first.status !== 401) return first;
  try {
    await api.get("/admin/data-questions/status");
  } catch {
    throw new Error(SESSION_EXPIRED_MESSAGE);
  }
  return post(body, signal);
};

const errorMessage = async (response: Response): Promise<string> => {
  const fallback = `No se pudo enviar la pregunta (HTTP ${response.status}).`;
  try {
    const data = (await response.json()) as {
      message?: string | string[];
      correlationId?: string;
    };
    const message = Array.isArray(data.message)
      ? data.message.join(" · ")
      : data.message;
    const ref = data.correlationId ? ` (ref: ${data.correlationId})` : "";
    return message && message !== "Internal server error"
      ? `${message}${ref}`
      : `${fallback}${ref}`;
  } catch {
    return fallback;
  }
};

/**
 * Sends one question and resolves when the server closes the stream after a
 * terminal `result` or `error` event. Every `step`, the `thread` id, and that
 * terminal event arrive through `onEvent`, in order. A stream that closes
 * without a terminal event (proxy idle timeout, dropped connection) rejects,
 * so the caller can tell the admin instead of silently losing the question.
 */
export const askDataQuestion = async (
  input: AskDataQuestionInput,
  onEvent: (event: DataQuestionStreamEvent) => void,
  signal?: AbortSignal
): Promise<void> => {
  const response = await authorizedPost(JSON.stringify(input), signal);
  if (!response.ok) {
    throw new Error(await errorMessage(response));
  }

  let terminal = false;
  const track = (event: DataQuestionStreamEvent) => {
    if (event.type === "result" || event.type === "error") terminal = true;
    onEvent(event);
  };

  const body = response.body;
  if (!body || typeof body.getReader !== "function") {
    // Runtimes without streaming bodies (jsdom): the whole payload at once.
    drainFrames(`${await response.text()}\n\n`, track);
  } else {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      buffer = drainFrames(buffer, track);
    }
    buffer += decoder.decode();
    drainFrames(`${buffer}\n\n`, track);
  }

  if (!terminal) {
    throw new Error(STREAM_CUT_MESSAGE);
  }
};
